import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Headphones, ChevronRight, Crown, Music, Zap, ExternalLink, Heart } from 'lucide-react';
import AccessLevelBadge from './AccessLevelBadge';
import { isGated } from '../utils/accessLabels';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import SignupInviteModal from './SignupInviteModal';
import AudioPlayer from './AudioPlayer';
import AccessRequiredModal from './AccessRequiredModal';

const optimizedCover = (url) => {
    if (!url || url.endsWith('-opt.webp')) return url;
    const dot = url.lastIndexOf('.');
    return dot !== -1 ? url.slice(0, dot) + '-opt.webp' : url;
};

const TopDeepWaveTracks = ({ limit = 5, showHeader = true, className = "", viewAllPosition = 'top' }) => {
    const [tracks, setTracks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [likedTracks, setLikedTracks] = useState(new Set());
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'audio' });
    const [accessModal, setAccessModal] = useState({ isOpen: false, track: null });
    const { isAuthenticated, hasActiveSubscription } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchTopTracks();
    }, [limit]);

    const fetchTopTracks = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get(`/upload/public/audio?limit=50`);

            if (response.data.success) {
                const deepWaveTracks = response.data.data.filter(t => t.user_id === 7);
                const sorted = deepWaveTracks
                    .sort((a, b) => (b.play_count || 0) - (a.play_count || 0))
                    .slice(0, limit);

                setTracks(sorted);

                if (isAuthenticated) {
                    const userLikes = new Set();
                    try {
                        const results = await Promise.all(sorted.map(async (track) => {
                            try {
                                const r = await api.get(`/upload/${track.id}/like-status`);
                                return { id: track.id, liked: r.data.success && r.data.liked };
                            } catch {
                                return { id: track.id, liked: track.user_has_liked || false };
                            }
                        }));
                        results.forEach(r => { if (r.liked) userLikes.add(r.id); });
                    } catch {
                        sorted.forEach(t => { if (t.user_has_liked) userLikes.add(t.id); });
                    }
                    setLikedTracks(userLikes);
                }
            } else {
                setError('Failed to load top tracks');
            }
        } catch (err) {
            console.error('Error fetching top Deep Wave tracks:', err);
            await fetchTopTracksLegacy();
        } finally {
            setLoading(false);
        }
    };

    const fetchTopTracksLegacy = async () => {
        try {
            const response = await api.get(`/upload/public/audio?limit=50`);
            if (response.data.success) {
                const sorted = response.data.data
                    .filter(t => t.user_id === 7)
                    .sort((a, b) => (b.play_count || 0) - (a.play_count || 0))
                    .slice(0, limit);
                setTracks(sorted);
            } else {
                setError('Failed to load top tracks');
            }
        } catch (err) {
            console.error('Error fetching top Deep Wave tracks (legacy):', err);
            setError('Unable to load tracks. Please try again later.');
        }
    };

    const getRankBadgeStyle = (rank) => {
        if (rank === 1) return 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-yellow-900';
        if (rank === 2) return 'bg-gradient-to-r from-gray-400 to-gray-500 text-gray-900';
        if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-700 text-amber-100';
        return 'bg-gradient-to-r from-blue-600 to-blue-800 text-white';
    };

    const handleLike = async (trackId) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'audio' });
            return;
        }
        try {
            const response = await api.post(`/upload/${trackId}/like`);
            if (response.data.success) {
                const { liked, likeCount } = response.data.data;
                setLikedTracks(prev => {
                    const next = new Set(prev);
                    liked ? next.add(trackId) : next.delete(trackId);
                    return next;
                });
                setTracks(prev => prev.map(t => t.id === trackId ? { ...t, like_count: likeCount } : t));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    if (loading) {
        return (
            <div className={`top-deepwave-tracks ${className}`}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Headphones className="w-6 h-6 text-blue-400" />
                        <h2 className="text-xl font-bold text-white">Top Tracks by The Deep Wave</h2>
                    </div>
                )}
                <div className="space-y-4">
                    {[...Array(limit)].map((_, i) => (
                        <div key={i} className="bg-gray-800/30 rounded-xl border border-gray-700/50 animate-pulse" style={{ height: '160px' }} />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`top-deepwave-tracks ${className}`}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Headphones className="w-6 h-6 text-blue-400" />
                        <h2 className="text-xl font-bold text-white">Top Tracks by The Deep Wave</h2>
                    </div>
                )}
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎵</div>
                    <p className="text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (tracks.length === 0) {
        return (
            <div className={`top-deepwave-tracks ${className}`}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Headphones className="w-6 h-6 text-blue-400" />
                        <h2 className="text-xl font-bold text-white">Top Tracks by The Deep Wave</h2>
                    </div>
                )}
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎼</div>
                    <p className="text-gray-400 mb-2">No tracks available yet</p>
                    <p className="text-sm text-gray-500">Check back soon for top tracks!</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`top-deepwave-tracks ${className}`}>
            {showHeader && (
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <Headphones className="w-6 h-6 text-blue-400" />
                            <h2 className="text-lg sm:text-xl font-bold text-white">Top Tracks by The Deep Wave</h2>
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                Trending
                            </span>
                        </div>
                        {viewAllPosition === 'top' && (
                            <Link
                                to="/feed/artist/TheDeepWave/audio?sort=views"
                                className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors self-start sm:self-auto"
                            >
                                View All
                                <ChevronRight className="w-4 h-4" />
                            </Link>
                        )}
                    </div>
                </div>
            )}

            <div className="space-y-4">
                {tracks.map((track, index) => (
                    <div
                        key={track.id}
                        className="bg-gray-800/30 hover:bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-blue-500/30 transition-all duration-300 overflow-hidden"
                    >
                        {/* Header: rank + title + actions */}
                        <div className="flex items-center gap-2 px-3 pt-3 pb-1">
                            <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold ${getRankBadgeStyle(index + 1)}`}>
                                {index === 0 ? <Crown className="w-3 h-3" /> : index + 1}
                            </div>
                            <h3 className="flex-1 font-semibold text-white truncate text-sm">
                                {track.title || `Track ${index + 1}`}
                            </h3>
                            <button
                                onClick={() => handleLike(track.id)}
                                className={`p-1 transition-colors ${likedTracks.has(track.id) ? 'text-red-400' : 'text-gray-400 hover:text-red-400'}`}
                                title={likedTracks.has(track.id) ? 'Liked' : 'Like'}
                            >
                                <Heart className={`w-3.5 h-3.5 ${likedTracks.has(track.id) ? 'fill-current' : ''}`} />
                            </button>
                            <Link
                                to={`/view/audio/${track.username || 'band'}/${track.stored_filename || track.original_filename || track.id}`}
                                className="p-1 text-gray-400 hover:text-blue-400 transition-colors"
                                title="View track page"
                            >
                                <ExternalLink className="w-3.5 h-3.5" />
                            </Link>
                        </div>

                        {/* AudioPlayer (locked overlay intercepts interaction when gated) */}
                        {(() => {
                            const gated = isGated(track.access_level, isAuthenticated, hasActiveSubscription);
                            return (
                                <div className="px-3 pb-2 relative">
                                    <AudioPlayer
                                        src={track.file_url}
                                        coverImageUrl={track.cover_image_url}
                                        title={track.title}
                                        uploadId={gated ? null : track.id}
                                        locked={gated}
                                        onLockedClick={() => setAccessModal({ isOpen: true, track })}
                                    />
                                    {gated && (
                                        <AccessLevelBadge accessLevel={track.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                    )}
                                </div>
                            );
                        })()}

                        {/* Stats row */}
                        <div className="flex items-center justify-between px-3 pb-3">
                            <span className="text-xs text-gray-500">
                                {(track.play_count || 0).toLocaleString()} plays · {(track.view_count || 0).toLocaleString()} views
                            </span>
                            <div className="flex items-center gap-0.5">
                                {[...Array(5)].map((_, i) => {
                                    const maxPlays = Math.max(1000, tracks[0]?.play_count || 1000);
                                    const level = Math.floor((track.play_count || 0) / maxPlays * 5);
                                    return (
                                        <div
                                            key={i}
                                            className={`w-1 rounded-full transition-all ${i < level ? 'bg-blue-400 h-4' : 'bg-gray-600 h-2'}`}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {viewAllPosition === 'bottom' ? (
                <div className="mt-6 text-center">
                    <Link
                        to="/feed/artist/TheDeepWave/audio?sort=views"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 rounded-lg transition-all duration-200 hover:scale-105 text-sm border border-blue-600/20"
                    >
                        <Music className="w-3.5 h-3.5" />
                        View All Top Tracks
                    </Link>
                </div>
            ) : tracks.length >= limit && (
                <div className="mt-4 text-center">
                    <Link
                        to="/feed/artist/TheDeepWave/audio?sort=views"
                        className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                        <Music className="w-4 h-4" />
                        View Full Top Charts
                        <ChevronRight className="w-4 h-4" />
                    </Link>
                </div>
            )}

            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'audio' })}
                contentType={signupInvite.contentType}
            />

            <AccessRequiredModal
                isOpen={accessModal.isOpen}
                onClose={() => setAccessModal({ isOpen: false, track: null })}
                accessLevel={accessModal.track?.access_level}
                action="listen"
                isAuthenticated={isAuthenticated}
                viewUrl={accessModal.track ? `/view/audio/${accessModal.track.username || 'band'}/${accessModal.track.stored_filename || accessModal.track.original_filename || accessModal.track.id}` : null}
            />
        </div>
    );
};

export default TopDeepWaveTracks;
